import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { projectsData } from '../data/projectsData';
import { CiSearch, CiLogout } from "react-icons/ci";
import { FaWhatsapp } from 'react-icons/fa';
import './ProjectDetails.css';

const ProjectDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [imageLoading, setImageLoading] = useState(false);
  const [mainImageLoading, setMainImageLoading] = useState(true);

  const isVideoFile = (url) => {
    return url.includes('.mp4') || url.includes('.webm') || url.includes('.mov') || url.includes('video/upload');
  };

  useEffect(() => {
    const projectData = projectsData.find(p => p.id === parseInt(id));
    if (projectData) {
      setProject(projectData);
    }
    setLoading(false);
  }, [id]);

  useEffect(() => {
    if (project && project.gallery && project.gallery[currentImageIndex]) {
      setMainImageLoading(true);
    }
  }, [currentImageIndex, project]);

  const handleWhatsAppClick = () => {
    const phoneNumber = '5519993489551';
    const message = 'Olá! Gostaria de saber mais sobre os serviços de arquitetura.';
    const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  const getOptimizedImageUrl = (baseUrl, width = 800, height = 600) => {
    if (baseUrl.includes('res.cloudinary.com') && baseUrl.includes('/upload/')) {
      return baseUrl.replace('/upload/', `/upload/w_${width},h_${height},c_fill,q_auto,f_auto/`);
    }
    return baseUrl;
  };

  const getOptimizedVideoUrl = (baseUrl, width = 800, height = 600) => {
    if (baseUrl.includes('res.cloudinary.com') && baseUrl.includes('/upload/')) {
      return baseUrl.replace('/upload/', `/upload/w_${width},h_${height},c_fit,q_auto/`);
    }
    return baseUrl;
  };

  const getFullImageUrl = (baseUrl) => {
    if (baseUrl.includes('res.cloudinary.com') && baseUrl.includes('/upload/')) {
      return baseUrl.replace('/upload/', `/upload/w_1920,h_1280,c_fill,q_auto,f_auto/`);
    }
    return baseUrl;
  };

  const getFullVideoUrl = (baseUrl) => {
    if (baseUrl.includes('res.cloudinary.com') && baseUrl.includes('/upload/')) {
      return baseUrl.replace('/upload/', `/upload/w_1920,h_1080,c_fit,q_auto/`);
    }
    return baseUrl;
  };

  const nextImage = useCallback((e) => {
    if (e) e.stopPropagation();
    if (!project) return;
    setImageLoading(true);
    setMainImageLoading(true);
    setCurrentImageIndex((prev) => 
      prev === project.gallery.length - 1 ? 0 : prev + 1
    );
  }, [project]);

  const prevImage = useCallback((e) => {
    if (e) e.stopPropagation();
    if (!project) return;
    setImageLoading(true);
    setMainImageLoading(true);
    setCurrentImageIndex((prev) => 
      prev === 0 ? project.gallery.length - 1 : prev - 1
    );
  }, [project]);

  const openLightbox = (index) => {
    setCurrentImageIndex(index);
    setImageLoading(true);
    setIsLightboxOpen(true);
  };

  const closeLightbox = () => {
    setIsLightboxOpen(false);
    setImageLoading(false);
  };

  const handleImageLoad = () => {
    setImageLoading(false);
  };

  const handleImageError = () => {
    setImageLoading(false);
  };

  const handleMainImageLoad = () => {
    setMainImageLoading(false);
  };

  const handleMainImageError = () => {
    setMainImageLoading(false);
  };

  const handleMainVideoLoad = () => {
    setMainImageLoading(false);
  };

  useEffect(() => {
    if (isLightboxOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isLightboxOpen]);

  useEffect(() => {
    const handleKeyPress = (e) => {
      if (!isLightboxOpen) return;
      
      if (e.key === 'Escape') {
        closeLightbox();
      } else if (e.key === 'ArrowLeft') {
        prevImage();
      } else if (e.key === 'ArrowRight') {
        nextImage();
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [isLightboxOpen, nextImage, prevImage]);

  if (loading) {
    return (
      <div className="project-loading">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="project-not-found">
        <h2>Projeto não encontrado</h2>
        <button onClick={() => navigate('/')} className="btn btn-primary">
          Voltar ao início
        </button>
      </div>
    );
  }

  const currentMedia = project.gallery[currentImageIndex];
  const isCurrentVideo = isVideoFile(currentMedia);

  return (
    <div className="project-details">
      {/* Header */}
      <header className="project-header">
        <div className="container">
          <button onClick={() => navigate('/#portfolio')} className="back-button">
            <CiLogout />
            Voltar ao Portfólio
          </button>
          <div className="project-title-section">
            <span className="project-subtitle">{project.subtitle}</span>
            <h1>{project.title}</h1>
          </div>
        </div>
      </header>

      {/* Galeria Principal */}
      <section className="project-main-gallery">
        <div className="main-image-container">
          {mainImageLoading && (
            <div className="main-image-loading">
              <div className="main-image-spinner"></div>
              <p>Carregando imagem...</p>
            </div>
          )}
          
          {isCurrentVideo ? (
            <video 
              src={getOptimizedVideoUrl(currentMedia, 1400, 800)}
              className={`main-gallery-video ${mainImageLoading ? 'loading' : ''}`}
              controls
              onClick={() => openLightbox(currentImageIndex)}
              onLoadedData={handleMainVideoLoad}
              onError={handleMainImageError}
            />
          ) : (
            <img 
              src={getOptimizedImageUrl(currentMedia, 1400, 800)}
              alt={`${project.title} - Imagem ${currentImageIndex + 1}`}
              className={`main-gallery-image ${mainImageLoading ? 'loading' : ''}`}
              onClick={() => openLightbox(currentImageIndex)}
              onLoad={handleMainImageLoad}
              onError={handleMainImageError}
            />
          )}
          
          {/* Logo marca d'água */}
          <img 
            src="/logotipo/BiancaPaulino_logotipo-JUNHO2025-BRANCO.png"
            alt="Logo" 
            className="watermark-logo"
          />
          
          {project.gallery.length > 1 && (
            <>
              <button className="gallery-nav prev" onClick={prevImage}>
                <span>‹</span>
              </button>
              <button className="gallery-nav next" onClick={nextImage}>
                <span>›</span>
              </button>
              <div className="gallery-counter">
                {currentImageIndex + 1} / {project.gallery.length}
              </div>
            </>
          )}
        </div>
      </section>

      {/* Grid */}
      <section className="project-thumbnails">
        <div className="container">
          <div className="thumbnails-grid">
            {project.gallery.map((media, index) => (
              <div
                key={index}
                className={`thumbnail-item ${index === currentImageIndex ? 'active' : ''}`}
                onClick={() => {
                  setMainImageLoading(true);
                  setCurrentImageIndex(index);
                }}
              >
                {isVideoFile(media) ? (
                  <video
                    src={getOptimizedVideoUrl(media, 300, 200)}
                    className="thumbnail-video"
                    muted
                  />
                ) : (
                  <img
                    src={getOptimizedImageUrl(media, 300, 200)}
                    alt={`Thumbnail ${index + 1}`}
                    className="thumbnail-image"
                  />
                )}
                <div className="thumbnail-overlay">
                  <span className="thumbnail-zoom">
                    {isVideoFile(media) ? '▶' : <CiSearch />}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="project-contact">
        <div className="container">
          <div className="contact-card">
            <h3>Gostou deste projeto?</h3>
            <p>Entre em contato conosco para discutir seu próximo projeto arquitetônico!</p>
            <button className="btn btn-primary" onClick={handleWhatsAppClick}>
            <FaWhatsapp className="whatsapp-icon" />
              Fale Conosco
            </button>
          </div>
        </div>
      </section>

      {/* Lightbox */}
      {isLightboxOpen && (
        <div className="lightbox-overlay" onClick={closeLightbox}>
          <div className="lightbox-container" onClick={(e) => e.stopPropagation()}>
            <button className="lightbox-close" onClick={closeLightbox}>×</button>
            
            <div className="lightbox-media">
              {imageLoading && !isCurrentVideo && (
                <div className="lightbox-loading">
                  <div className="lightbox-spinner"></div>
                  <p>Carregando imagem...</p>
                </div>
              )}
              
              {isCurrentVideo ? (
                <video 
                  src={getFullVideoUrl(currentMedia)}
                  className="lightbox-video"
                  controls
                  autoPlay
                  onClick={(e) => e.stopPropagation()}
                  onLoadedData={() => setImageLoading(false)}
                />
              ) : (
                <img 
                  src={getFullImageUrl(currentMedia)}
                  alt={`${project.title} - Imagem ${currentImageIndex + 1}`}
                  className={`lightbox-image ${imageLoading ? 'loading' : ''}`}
                  onClick={(e) => e.stopPropagation()}
                  onLoad={handleImageLoad}
                  onError={handleImageError}
                />
              )}
              
              {/* Logo marca d'água no lightbox */}
              <img 
                src="/logotipo/BiancaPaulino_logotipo-JUNHO2025-BRANCO.png"
                alt="Logo" 
                className="lightbox-watermark"
              />
            </div>
            
            {project.gallery.length > 1 && (
              <>
                <button className="lightbox-nav prev" onClick={prevImage}>‹</button>
                <button className="lightbox-nav next" onClick={nextImage}>›</button>
                <div className="lightbox-info">
                  <div className="lightbox-counter">
                    {currentImageIndex + 1} / {project.gallery.length}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectDetails;